import * as yup from 'yup';
import moment from 'moment';

export const milestoneSchema = yup
  .object()
  .shape({
    title: yup.string().required('Title is required'),
    description: yup
      .mixed()
      .required('Description is required')
      .test('has-content', 'Description is required', (value) => {
        // Handle empty cases
        if (!value || value === '') return false;

        // If it's already a string with content, it's valid
        if (typeof value === 'string' && value.length > 0) {
          // Check if it's a JSON string from RTEditor
          try {
            const parsed = JSON.parse(value);
            if (parsed?.root?.children) {
              // Check if there's actual text content in the JSON
              const hasText = (nodes) => {
                for (const node of nodes || []) {
                  if (node.type === 'text' && node.text && node.text.trim().length > 0) {
                    return true;
                  }
                  if (node.children && hasText(node.children)) {
                    return true;
                  }
                }
                return false;
              };
              return hasText(parsed.root.children);
            }
          } catch (e) {
            // If it's not JSON, treat as plain text
            return value.trim().length > 0;
          }
        }

        // If it's a Lexical object directly (before RTEditor processes it)
        if (typeof value === 'object' && value?.root?.children) {
          const hasText = (nodes) => {
            for (const node of nodes || []) {
              if (node.type === 'text' && node.text && node.text.trim().length > 0) {
                return true;
              }
              if (node.children && hasText(node.children)) {
                return true;
              }
            }
            return false;
          };
          return hasText(value.root.children);
        }

        return false;
      }),
    start_date: yup
      .mixed()
      .nullable()
      .required('Start date is required')
      .test('is-valid-date', 'Start date is required', (v) => v !== null && moment(v).isValid())
      .test('not-in-past', 'Start date cannot be in the past', (v) => {
        if (!v) return false;
        const start = moment(v).startOf('day');
        const today = moment().startOf('day');
        return start.isSameOrAfter(today);
      })
      .test('not-too-far-future', 'Start date cannot be more than 3 months from today', (v) => {
        if (!v) return false;
        const start = moment(v).startOf('day');
        const threeMonthsFromNow = moment().add(3, 'months').startOf('day');
        return start.isSameOrBefore(threeMonthsFromNow);
      }),
    end_date: yup
      .mixed()
      .nullable()
      .required('End date is required')
      .test('is-valid-date', 'End date is required', (v) => v !== null && moment(v).isValid())
      .test('after-start', 'End date must be after start date', function (v) {
        const { start_date } = this.parent;
        if (!v || !start_date) return false;
        return moment(v).startOf('day').isAfter(moment(start_date).startOf('day'));
      })
      .test('not-today-or-past', 'End date cannot be today or in the past', (v) => {
        if (!v) return false;
        const end = moment(v).startOf('day');
        const today = moment().startOf('day');
        return end.isAfter(today);
      })
      .test('not-far-future', 'End date seems too far in the future', (v) => {
        if (!v) return false;
        // Arbitrary guard: 3 years from now
        const end = moment(v).startOf('day');
        const limit = moment().add(3, 'years').startOf('day');
        return end.isSameOrBefore(limit);
      }),
    estimated_hours: yup
      .number()
      .typeError('Estimated hours must be a number')
      .required('Estimated hours are required')
      .min(0.5, 'Estimated hours must be at least 0.5')
      .test(
        'valid-step',
        'Estimated hours must be 0.5 (half hour) or 1 (one hour) or multiples thereof',
        (value) => {
          if (value === undefined || value === null) return false;
          // Check if value is a valid step: integer or decimal part is 0 or 0.5
          const decimalPart = value % 1;
          const validDecimals = [0, 0.5];
          // Use a small epsilon for floating point comparison
          return validDecimals.some(
            (valid) => Math.abs(decimalPart - valid) < 0.001
          );
        }
      )
      .test('fits-in-duration', function (value) {
        const { duration, duration_unit } = this.parent;

        // Skip validation if any required field is missing
        if (!duration || !duration_unit || !value) return true;

        // Ensure values are numbers
        const durationNum = typeof duration === 'number' ? duration : Number(duration);
        const estimatedHoursNum = typeof value === 'number' ? value : Number(value);

        // Skip validation if conversion failed
        if (isNaN(durationNum) || isNaN(estimatedHoursNum)) return true;

        // Convert duration to maximum possible working hours (freelancer perspective)
        let maxPossibleHours;
        switch (duration_unit) {
          case 'days':
            maxPossibleHours = durationNum * 12; // Freelancers can work up to 12 hours per day
            break;
          case 'weeks':
            maxPossibleHours = durationNum * 60; // 5 days * 12 hours per week
            break;
          case 'months':
            maxPossibleHours = durationNum * 240; // 4 weeks * 60 hours per month
            break;
          default:
            return true; // Skip validation for unknown units
        }

        // Hard validation: Estimated hours cannot exceed maximum possible working hours in duration
        if (estimatedHoursNum > maxPossibleHours) {
          return this.createError({
            message: `Estimated hours (${estimatedHoursNum}h) cannot exceed maximum possible working hours in ${durationNum} ${duration_unit} (${maxPossibleHours}h)`
          });
        }

        return true;
      }),
    duration: yup
      .number()
      .typeError('Duration must be a number')
      .required('Duration is required')
      .moreThan(0, 'Duration must be greater than 0'),
    duration_unit: yup
      .string()
      .required('Duration unit is required')
      .oneOf(['days', 'weeks', 'months'], 'Invalid duration unit')
      .default('days'),
  });
